import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSectionScroll, useScrollToTop } from '../hooks/useSectionScroll';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigationBar from '../components/layout/NavigationBar';
import Footer from '../components/layout/Footer';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

function Coverage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState({});

  useScrollToTop();
  useSectionScroll();

  const jurisdictions = ['croatia', 'austria', 'eu'];

  // Check URL parameters on mount
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const expandParam = params.get('expand');

    if (expandParam === 'croatia-courts') {
      // Automatically expand Croatian courts (index 1)
      setExpanded({ 'croatia-1': true });

      // Scroll to Croatian section after a short delay
      setTimeout(() => {
        const croatiaSection = document.querySelector('[data-jurisdiction="croatia"]');
        if (croatiaSection) {
          croatiaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else if (expandParam === 'croatia-regulators') {
      // Automatically expand Croatian regulators (index 2)
      setExpanded({ 'croatia-2': true });

      // Scroll to Croatian section after a short delay
      setTimeout(() => {
        const croatiaSection = document.querySelector('[data-jurisdiction="croatia"]');
        if (croatiaSection) {
          croatiaSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [location.search]);

  const handleExpandClick = (jurisdiction, source) => {
    const key = `${jurisdiction}-${source}`;
    setExpanded(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ backgroundColor: colors.offWhite, minHeight: '100vh' }}>
        <NavigationBar />

        {/* Spacer for fixed navbar */}
        <Box sx={{ height: '64px' }} />

        {/* Hero Section */}
        <Box
          sx={{
            py: 8,
            backgroundColor: colors.yellow,
            textAlign: 'center',
            borderBottom: `2px solid ${colors.red}`,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                color: colors.darkRed,
                mb: 2,
                fontWeight: 'bold',
              }}
            >
              {t('coverage.title')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.darkRed,
                mb: 4,
              }}
            >
              {t('coverage.subtitle')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const element = document.getElementById('try');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              sx={{
                backgroundColor: colors.red,
                color: colors.yellow,
                fontWeight: 'bold',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: colors.darkRed },
              }}
            >
              {t('hero.get_started') || 'Request Access'}
            </Button>
          </Container>
        </Box>

        {/* Jurisdictions Grid */}
        <Container maxWidth="lg" sx={{ pb: 8, pt: 4 }}>
          <Grid container spacing={4}>
            {jurisdictions.map((jurisdiction) => (
              <Grid item xs={12} md={4} key={jurisdiction}>
                <Paper
                  elevation={3}
                  data-jurisdiction={jurisdiction}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: 'white',
                    borderTop: `4px solid ${colors.red}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.darkRed,
                      fontWeight: 'bold',
                      mb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {t(`coverage.${jurisdiction}.title`)}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.darkRed,
                      mb: 3,
                      textAlign: 'center',
                      fontSize: '1.1rem',
                      opacity: 0.9,
                    }}
                  >
                    {t(`coverage.${jurisdiction}.subtitle`)}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.red,
                      fontWeight: 'bold',
                      mb: 3,
                    }}
                  >
                    Sources
                  </Typography>

                  <List dense>
                    {t(`coverage.${jurisdiction}.sources`, { returnObjects: true }).map((source, index) => {
                      const sourceKey = `${jurisdiction}-${index}`;
                      const hasSubItems = jurisdiction === 'croatia' && (index === 1 || index === 2) ?
                        t(`coverage.${jurisdiction}.sourceDetails.${index}`, { returnObjects: true, defaultValue: null }) :
                        null;
                      const isExpanded = expanded[sourceKey] || false;

                      return (
                        <React.Fragment key={index}>
                          <ListItem
                            sx={{
                              pl: 0,
                              pr: hasSubItems && Array.isArray(hasSubItems) ? 0 : 2,
                              cursor: hasSubItems && Array.isArray(hasSubItems) ? 'pointer' : 'default',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                              '&:hover': hasSubItems && Array.isArray(hasSubItems) ? {
                                backgroundColor: 'rgba(69, 26, 21, 0.04)',
                              } : {},
                            }}
                            onClick={hasSubItems && Array.isArray(hasSubItems) ?
                              () => handleExpandClick(jurisdiction, index) :
                              undefined
                            }
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <CheckCircleIcon sx={{ color: colors.yellow, fontSize: 20 }} />
                            </ListItemIcon>
                            {hasSubItems && Array.isArray(hasSubItems) && (
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpandClick(jurisdiction, index);
                                }}
                                sx={{
                                  p: 0.5,
                                  mr: 1,
                                  backgroundColor: isExpanded ? colors.offWhite : 'transparent',
                                  '&:hover': {
                                    backgroundColor: colors.offWhite,
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                {isExpanded ?
                                  <ExpandLessIcon sx={{
                                    color: colors.darkRed,
                                    fontSize: 24,
                                    transition: 'transform 0.2s ease',
                                  }} /> :
                                  <ExpandMoreIcon sx={{
                                    color: colors.darkRed,
                                    fontSize: 24,
                                    transition: 'transform 0.2s ease',
                                  }} />
                                }
                              </IconButton>
                            )}
                            <ListItemText
                              primary={source}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: {
                                  color: colors.darkRed,
                                  fontWeight: hasSubItems && Array.isArray(hasSubItems) ? 500 : 400,
                                },
                              }}
                            />
                          </ListItem>
                          {hasSubItems && Array.isArray(hasSubItems) && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <List dense sx={{ pl: 4 }}>
                                {hasSubItems.map((subItem, subIndex) => (
                                  <ListItem key={subIndex} sx={{ pl: 2, py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: '50%',
                                          backgroundColor: colors.darkRed,
                                        }}
                                      />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={subItem}
                                      primaryTypographyProps={{
                                        variant: 'body2',
                                        sx: { color: colors.darkRed, fontSize: '0.875rem' },
                                      }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Footer />
      </Box>
    </>
  );
}

export default Coverage;
